import {
  evidencePromptsDismissAtom,
  evidencePromptsFaviconsAtom,
  evidencePromptsSuggestionsAtom,
  evidencePromptsVisibleAtom,
} from '@/atoms/evidenceSidebarAtoms';
import type { EvidencePromptSuggestion } from '@/components/ui/floating-ask-heidi/evidence-prompts-bar';
import type { PinnedChip } from '@/components/ui/floating-ask-heidi/evidence-settings-popover';
import {
  sortChipsByPriority,
  type SuggestedChip,
} from '@/components/ui/transcript-chips/types';
import { useIsEvidencePromptsBarEnabled } from '@/features/flags';
import { useManualExposure } from '@/hooks/amplitude';
import { useQuerySourceRecommendations } from '@/hooks/query/useQuerySourceRecommendations';
import type { SessionStage } from '@/hooks/useSessionStage';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { atom, useAtom, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

/**
 * Session-scoped dismissal — scoped per session so dismissing in one
 * session doesn't affect another.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const evidencePromptsBarDismissedAtomFamily = atomFamily((sessionId: string) =>
  atom(false)
);

interface UseEvidencePromptsBarOptions {
  sessionId: string;
  sessionStage: SessionStage;
  suggestedChips: SuggestedChip[];
  pinnedChips: PinnedChip[];
  // When true, a higher-priority surface (e.g. source recommendation banner or
  // evidence summary suggestion) owns the slot, so the prompts bar must stay
  // hidden and suppress its visibility atom, exposure, and "shown" analytics.
  isBlocked?: boolean;
}

function chipToSuggestion(
  chip: SuggestedChip | PinnedChip,
  isPinned: boolean
): EvidencePromptSuggestion {
  return {
    id: chip.id,
    label: chip.label,
    query: chip.query,
    category: chip.category,
    isPinned,
    // Forward the chip's description so the panel can render a
    // per-card rationale instead of falling back to the generic
    // category description.
    reason: chip.description,
  };
}

export function useEvidencePromptsBar({
  sessionId,
  sessionStage,
  suggestedChips,
  pinnedChips,
  isBlocked = false,
}: UseEvidencePromptsBarOptions) {
  const intl = useIntl();
  const isFlagEnabled = useIsEvidencePromptsBarEnabled();
  const [isDismissed, setIsDismissed] = useAtom(
    evidencePromptsBarDismissedAtomFamily(sessionId)
  );
  const [expanded, setExpanded] = useState(false);

  // Reset expanded state when session changes
  useEffect(() => {
    setExpanded(false);
  }, [sessionId]);

  // Source favicons from recommendation API
  const { data: recommendationsData } = useQuerySourceRecommendations(isFlagEnabled);
  const sourceFavicons = useMemo(
    () => (recommendationsData?.sources ?? []).map((s) => ({ domain: s.domain })),
    [recommendationsData?.sources]
  );

  // Persist last during-session suggestions for post-session fallback, scoped by sessionId
  const lastDuringSuggestionsRef = useRef<{
    sessionId: string;
    suggestions: EvidencePromptSuggestion[];
  }>({ sessionId: '', suggestions: [] });

  // Pre-session: hardcoded suggestions for patient preparation
  const preSessionSuggestions: EvidencePromptSuggestion[] = useMemo(
    () => [
      {
        id: 'pre-session-history',
        label: intl.formatMessage({
          defaultMessage: "Summarise this patient's relevant history",
          id: 'IfZEcpfQ11',
        }),
        query: intl.formatMessage({
          defaultMessage:
            "Summarise this patient's relevant history, current conditions, and active medications.",
          id: 'FvVXX7jW2Z',
        }),
        category: 'clinical_review',
        isPinned: true,
      },
      {
        id: 'pre-session-questions',
        label: intl.formatMessage({
          defaultMessage: 'Generate consultation questions',
          id: 'ZJvZI7IWq6',
        }),
        query: intl.formatMessage({
          defaultMessage:
            "What questions should I ask this patient in today's session based on their history?",
          id: 'gPRF+0azLF',
        }),
        category: 'suggested_question',
        isPinned: true,
      },
      {
        id: 'pre-session-treatment',
        label: intl.formatMessage({
          defaultMessage: 'Review current treatment plan',
          id: 'uE/vkcM4u9',
        }),
        query: intl.formatMessage({
          defaultMessage:
            "What's the current treatment plan, and what should I address today?",
          id: 'QWlDWxUR9d',
        }),
        category: 'plan',
        isPinned: true,
      },
    ],
    [intl]
  );

  // Return the full ordered list. Downstream consumers are
  // responsible for their own display caps (the EvidencePromptsBar
  // shows its top 3 chips).
  const suggestions = useMemo(() => {
    // During and post-session: prefer dynamic transcript-based suggestions
    if (
      (sessionStage === 'during' || sessionStage === 'post') &&
      suggestedChips.length > 0
    ) {
      const sorted = sortChipsByPriority([...suggestedChips]);
      return sorted.map((c) => chipToSuggestion(c, false));
    }

    if (sessionStage === 'post') {
      // Fall back to persisted during-session suggestions, then pinned
      if (
        lastDuringSuggestionsRef.current.sessionId === sessionId &&
        lastDuringSuggestionsRef.current.suggestions.length > 0
      ) {
        return lastDuringSuggestionsRef.current.suggestions;
      }
      return pinnedChips.map((c) => chipToSuggestion(c, true));
    }

    // Pre-session: use hardcoded patient preparation suggestions
    return preSessionSuggestions;
  }, [sessionId, sessionStage, suggestedChips, pinnedChips, preSessionSuggestions]);

  // Persist during-session suggestions for post-session fallback (outside useMemo to avoid side effects)
  useEffect(() => {
    if (sessionStage === 'during' && suggestions.length > 0) {
      lastDuringSuggestionsRef.current = { sessionId, suggestions };
    }
  }, [sessionId, sessionStage, suggestions]);

  // Dynamic placeholder per session stage
  const placeholderText = useMemo(() => {
    switch (sessionStage) {
      case 'pre':
        return intl.formatMessage({
          defaultMessage: 'Prepare or check anything...',
          id: 'dIE/76hW8s',
        });
      case 'during':
        return intl.formatMessage({
          defaultMessage: 'Check anything...',
          id: 'pnRQIUmYYp',
        });
      case 'post':
        return intl.formatMessage({
          defaultMessage: 'Check, edit or write anything...',
          id: 'U7vOwiRim6',
        });
    }
  }, [sessionStage, intl]);

  const shouldShow =
    isFlagEnabled && !isDismissed && !isBlocked && suggestions.length > 0;
  const isExposureEligible = !isDismissed && !isBlocked && suggestions.length > 0;

  // Sync to shared atoms so the sidebar panel can read the same state (GRO-2829)
  const setSharedSuggestions = useSetAtom(evidencePromptsSuggestionsAtom);
  const setSharedFavicons = useSetAtom(evidencePromptsFaviconsAtom);
  const setSharedVisible = useSetAtom(evidencePromptsVisibleAtom);
  useEffect(() => {
    setSharedSuggestions(suggestions);
    setSharedFavicons(sourceFavicons);
    setSharedVisible(shouldShow);
    return () => {
      setSharedSuggestions([]);
      setSharedFavicons([]);
      setSharedVisible(false);
    };
  }, [
    suggestions,
    sourceFavicons,
    shouldShow,
    setSharedSuggestions,
    setSharedFavicons,
    setSharedVisible,
  ]);

  // Manual exposure for Amplitude experiment analysis (GRO-2764)
  useManualExposure({
    flagKey: 'release-evidence-enable-clinical-suggestions-bar',
    isEnabled: isFlagEnabled,
    isVisible: isExposureEligible,
  });

  // Track bar shown (fire once per session)
  const hasTrackedShown = useRef(false);
  useEffect(() => {
    hasTrackedShown.current = false;
  }, [sessionId]);

  useEffect(() => {
    if (shouldShow && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      track({
        event_name: TrackingEventName.EvidencePromptsBarShown,
        props: {
          session_id: sessionId,
          session_stage: sessionStage,
          suggestions_count: suggestions.length,
          source_count: sourceFavicons.length,
        },
        options: { amplitude: false },
      });
    }
  }, [shouldShow, sessionId, sessionStage, suggestions.length, sourceFavicons.length]);

  const onDismiss = useCallback(() => {
    setIsDismissed(true);
    setExpanded(false);
    track({
      event_name: TrackingEventName.EvidencePromptsBarDismissed,
      props: {
        session_id: sessionId,
        session_stage: sessionStage,
      },
      options: { amplitude: false },
    });
  }, [setIsDismissed, sessionId, sessionStage]);

  // Sync dismiss callback so the sidebar panel can call the same dismiss (GRO-2829)
  const setSharedDismiss = useSetAtom(evidencePromptsDismissAtom);
  useEffect(() => {
    setSharedDismiss(() => onDismiss);
    return () => setSharedDismiss(null);
  }, [onDismiss, setSharedDismiss]);

  const onExpandedChange = useCallback(
    (value: boolean) => {
      setExpanded(value);
      if (value) {
        track({
          event_name: TrackingEventName.EvidencePromptsBarExpanded,
          props: {
            session_id: sessionId,
            session_stage: sessionStage,
            suggestions_count: suggestions.length,
          },
          options: { amplitude: false },
        });
      }
    },
    [sessionId, sessionStage, suggestions.length]
  );

  const onReopen = useCallback(() => {
    setIsDismissed(false);
    setExpanded(false);
  }, [setIsDismissed]);

  return {
    isEnabled: isFlagEnabled,
    shouldShow,
    isDismissed,
    sessionStage,
    suggestions,
    sourceFavicons,
    sourceCount: recommendationsData?.sources?.length ?? 0,
    placeholderText,
    expanded,
    onExpandedChange,
    onDismiss,
    onReopen,
  };
}
